import React, { useState, useRef, useEffect, useCallback } from 'react'
import './Signup.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser,
  faEnvelopeCircleCheck,
  faCheckCircle,
  faDollarSign,
  faChevronLeft,
  faChevronRight,
  faCheckSquare,
} from '@fortawesome/free-solid-svg-icons'
import { faSquare } from '@fortawesome/free-regular-svg-icons'
import { useForm, Controller } from 'react-hook-form'
import StepNavigation from '../../common/components/StepNavigation'
import StateSelector from '../../common/components/StateSelector'
import CountrySelector from '../../common/components/CountrySelector'
import FormSectionHeader from '../../common/components/FormSectionHeader'
import SportSelector from '../../common/components/SportSelector'
import Tooltip from '../../common/components/Tooltip'
import BlockOptions from '../../common/components/BlockOptions'
import InstantConnect from '../../common/components/InstantConnect'
import LoadingSpinner from '../../common/components/LoadingSpinner'
import Confetti from '../../common/components/Confetti'
import DOBSelector from '../../common/components/DOBSelector/DOBSelector'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import cx from 'classnames'
import {
  useUTMParams,
  useAmbassadorParams,
  useAwinParams,
  sellerOptions,
  volumeOptions,
  getAuthRedirectURI,
  getDeviceType,
  isServerRender,
} from '../../utils'
import { useTrackingCode } from 'react-hubspot-tracking-code-hook'
import ReCAPTCHA from 'react-google-recaptcha'
import auth0 from 'auth0-js'
import * as Sentry from '@sentry/browser'
import LogIn from '../../common/components/LogIn'
import Helmet from 'react-helmet'
import PhoneInput from 'react-phone-number-input'
import NationalIdInput from '../../common/components/NationalIdInput'
import 'react-phone-number-input/style.css'
import {
  isValidPhoneNumber,
  formatPhoneNumberIntl,
} from 'react-phone-number-input/max'
import { useCookiebot } from '../../utils/CookiebotContext'

// Maps form seller_type codes to HubSpot lysted_use_case enumeration values.
// Values must match the HubSpot property options EXACTLY (case- and
// punctuation-sensitive) or HubSpot silently drops them.
const HUBSPOT_USE_CASE = {
  large: 'Established Ticket Broker',
  broker: 'New Ticket Broker',
  season: 'Season Ticket Holder',
  small: 'One-Time/Occasional Seller',
}

// The terminal step of the flow. Reaching it submits the signup, and it is
// also where the email-verification instructions and the (Large-broker only)
// "Talk to our team" sales options are shown together. Instant Connect used to
// be its own step *before* this one, which meant a broker who engaged with the
// sales options and left never actually completed signup.
const VERIFICATION_STEP = 4

const validatePostalCode = (value, getValues) => {
  const country = getValues('country.value')
  if (!value) {
    return 'Required'
  }
  if (country === 'US' && !/^\d{5}$/.test(value)) {
    return 'Zip code must be 5 digits'
  }
  return true
}

function Signup({ isMobile }) {
  const recaptchaRef = useRef()
  // Synchronous double-submit guard for handleSignup. `submitting` is state, so
  // it flips a tick late — and post-merge the Large POST fires from
  // handleVolumeSelected, an auto-advancing option tile that gets double-tapped
  // on mobile in a way a submit button doesn't. Two clicks in one frame would
  // both POST: the second returns 204 (yanking the broker to Auth0) or 409
  // (replacing the completion checklist with an error), showing a failure for
  // an account that was in fact created. Mirrors `submittingRef` in
  // InstantConnect.
  const signupSubmittedRef = useRef(false)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setError,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      country: { value: 'US', label: 'United States' },
    },
  })

  const location = useLocation()

  const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent, utmName } =
    useUTMParams(location)
  const { mbsySource, mbsyCampaign, mbsyexp, shortcode } =
    useAmbassadorParams(location)
  const awinUtm = useAwinParams(location)
  const navigate = useNavigate()

  const [steps, setSteps] = useState([
    {
      id: 1,
      name: 'Registration',
      icon: faUser,
      completed: false,
      visible: true,
    },
    { id: 2, name: 'Ticketing', icon: null, completed: false, visible: false },
    { id: 3, name: 'dynamic', icon: null, completed: false, visible: false },
    {
      id: 4,
      name: 'Verification',
      icon: faEnvelopeCircleCheck,
      completed: false,
      visible: true,
    },
    {
      id: 5,
      name: 'Activation',
      icon: faCheckCircle,
      completed: false,
      visible: true,
    },
    {
      id: 6,
      name: 'Earn',
      icon: faDollarSign,
      completed: false,
      visible: true,
    },
  ])

  const { setTrackEvent, setIdentity, setPathPageView } = useTrackingCode()

  const [currentStep, setCurrentStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState('')
  const [selectedCountry, setSelectedCountry] = useState({
    value: 'US',
    label: 'United States',
  })
  const [selectorStates, setSelectorStates] = useState({
    country: {
      focused: false,
      filled: true,
    },
    state: {
      focused: false,
      filled: false,
    },
  })
  const [sellerTypeNextStep, setSellerTypeNextStep] = useState(null)
  const [phoneHasValue, setPhoneHasValue] = useState(false)
  const [phoneIsFocused, setPhoneIsFocused] = useState(false)

  const { isStatisticsAllowed, isMarketingAllowed } = useCookiebot()

  const checkPhoneValue = value => {
    if (value) {
      setPhoneHasValue(true)
    } else {
      setPhoneHasValue(false)
    }
  }

  const handleBack = e => {
    e.preventDefault()
    if (currentStep === 1) {
      navigate('/')
    } else {
      // Back is only reachable from steps 2-3 (the footer is hidden on the
      // terminal verification step), and each of those steps' predecessor is
      // simply the one before it, so a plain decrement is correct.
      setCurrentStep(currentStep - 1)
    }

    if (sellerTypeNextStep === 'season') {
      setValue('league', null)
      setValue('team', null)
    }

    document?.documentElement.scrollTo(0, 0)
  }

  const updateSteps = (increment = 1) => {
    const updatedSteps = steps.map(step => {
      if (step.id === currentStep) {
        return { ...step, completed: true }
      }
      return step
    })
    setSteps(updatedSteps)
    setCurrentStep(currentStep + increment)
  }

  const handleSellerTypeSelected = type => {
    const shouldSetType = ['large', 'season'].includes(type)
    // Large/season sellers go to the volume/sport step (3); everyone else
    // has no intermediate steps left and goes straight to signup.
    let increment = shouldSetType ? 1 : 2
    setSellerTypeNextStep(shouldSetType ? type : null)
    updateSteps(increment)
    if (increment === 2) {
      handleSignup()
    }
  }

  const handleVolumeSelected = volume => {
    handleNextStep()
  }

  const handleContinue = async data => {
    // The terminal step has no Continue button, but it does contain the
    // Instant Connect callback field — pressing Enter there would otherwise
    // submit this form and fire a second signup request.
    if (currentStep >= VERIFICATION_STEP) {
      return
    }
    if (currentStep < steps.length && !Object.keys(errors).length) {
      if (currentStep === 2) {
        handleSellerTypeSelected(sellerTypeNextStep)
      } else {
        handleNextStep()
      }
    }
  }

  const memoizedGetAuthRedirectURI = useCallback(() => {
    return getAuthRedirectURI(location)
  }, [location])

  const webauth = new auth0.WebAuth({
    audience: 'https://lysted.com/api',
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
    redirectUri: memoizedGetAuthRedirectURI(),
    responseType: 'code',
    leeway: 60,
    scope: 'openid profile email',
  })

  const handleSignup = async () => {
    // Set before setSubmitting and before any await, so a second call in the
    // same frame bails out rather than firing a duplicate POST.
    if (signupSubmittedRef.current) {
      return
    }
    signupSubmittedRef.current = true
    try {
      setSubmitting(true)
      const data = getValues()

      const volume = data.seller_type === 'large' ? data.sales_volume : null
      const source = shortcode
        ? `ambassador${utmSource ? `-${utmSource}` : ''}`
        : utmSource || 'NOT_SET'
      const team =
        data.seller_type === 'season'
          ? `${data.league?.sport || 'other'}|${data.league?.league || 'other'}|${data.team || 'other'}`
          : null

      if (!data.dobYear || !data.dobMonth || !data.dobDay) {
        throw new Error('Please select a valid date of birth')
      }

      // Pad both month and day values to get a format of YYYY-MM-DD
      const dobString = `${data.dobYear}-${data.dobMonth.toString().padStart(2, '0')}-${data.dobDay.toString().padStart(2, '0')}`

      let payload = {
        source,
        utm: {
          source,
          ...(utmMedium && { medium: utmMedium }),
          ...(utmCampaign && { campaign: utmCampaign }),
          ...(utmTerm && { term: utmTerm }),
          ...(utmContent && { content: utmContent }),
          ...(utmName && { name: utmName }),
          ...(shortcode && { shortcode }),
          ...(shortcode && {
            mbsy: {
              source: mbsySource || 'NOT_SET',
              campaign: mbsyCampaign || 'NOT_SET',
              exp: mbsyexp || 'NOT_SET',
            },
          }),
          // Assign awinUtm if it has params set, otherwise assign an empty object
          awin: awinUtm && typeof awinUtm === 'object' ? { ...awinUtm } : {},
        },
        first: data.first,
        last: data.last,
        dob: dobString,
        consentToVerify: data.agreement,
        consent_tos: !!data.agreement,
        // Existing consent slots are reused: consent_sms now records the
        // informational checkbox, consent_email the marketing checkbox.
        consent_sms: !!data.consent_informational,
        consent_email: !!data.consent_comms,
        email: data.email,
        phone: formatPhoneNumberIntl(data.phone),
        ...(data.company && { company: data.company }),
        address: {
          address_1: data.address_1,
          ...(data.address_2 && { address_2: data.address_2 }),
          city: data.city,
          state: data.state.value,
          postal_code: data.postal_code,
          country: data.country.value,
        },
        seller_type: data.seller_type,
        ...(volume && { volume }),
        ...(team && { team }),
      }

      if (data?.nationalId?.idType && data?.nationalId?.idNumber) {
        payload.nationalIdType = data.nationalId.idType
        payload.nationalIdNumber = data.nationalId.idNumber
      }

      const token = await recaptchaRef.current.executeAsync()
      payload.token = token

      // API request
      const response = await axios.post(
        `${process.env.REACT_APP_SIGNUP_URI}/signup`,
        payload
      )

      if (response.status === 201) {
        setServerError(null)
        try {
          // HubSpot Contact generation
          if (isStatisticsAllowed) {
            // Only send keys that are real HubSpot contact property internal names.
            // Passing the raw form object risks silent server-side rejection
            // (invalid keys, nested objects).
            setIdentity(data.email, {
              firstname: data.first,
              lastname: data.last,
              phone: formatPhoneNumberIntl(data.phone) || data.phone,
              lysted_use_case: HUBSPOT_USE_CASE[data.seller_type],
              lysted_consent_email: data.consent_comms
                ? 'Opted In'
                : 'Opted Out',
              lysted_signup_device_type: getDeviceType(),
              datateam_lysted_sign_up_date__c: Date.now(),
              company: data.company || '',
              address: [data.address_1, data.address_2]
                .filter(Boolean)
                .join(', '),
              city: data.city || '',
              state: data.state?.value || '',
              zip: data.postal_code || '',
              country: data.country?.value || '',
              ...(data.seller_type === 'large' && data.sales_volume
                ? { annual_sales_volume_lysted: data.sales_volume }
                : {}),
              ...(data.seller_type === 'season'
                ? {
                    lysted_signup_league: data.league?.league || '',
                    lysted_signup_team: data.team || '',
                  }
                : {}),
            })
            setTrackEvent({
              eventId: `${process.env.NODE_ENV !== 'production' ? 'TEST_' : ''}LYSTED_SIGNUP`,
              value: 'COMPLETED',
            })
            // Flush the buffered identity to HubSpot. identify/trackEvent alone do not
            // transmit on this portal tier; a pageview is the transmitting call that
            // sends the identity and triggers contact creation. Do not remove.
            setPathPageView('/signup/completed')
            // GA Conversion event
            if (!isServerRender && !!window.dataLayer) {
              // @note gtag is not set when using GTM to laod GA so we must use dataLayer
              // @note window.dataLayer existing doesn't mean GA is loaded
              window.dataLayer.push({
                event: 'conversion',
                send_to: 'AW-11164999022/7-8wCJHurOUYEO668csp',
              })
              window.dataLayer.push({
                event: 'lysted-successful-signup',
              })
            }
          }
          // Reddit pixel event
          if (isMarketingAllowed && !isServerRender && !!window.rdt) {
            window.rdt(
              'track',
              `SignUp${process.env.NODE_ENV !== 'production' ? '_TEST' : ''}`
            )
          }
        } catch (e) {
          Sentry.captureMessage('3P_FAILURE', {
            extra: {
              error: e?.toString(),
            },
          })
        }
      } else if (response.status === 204) {
        // Account exists, push to Auth
        webauth.authorize({
          'ext-uuid': window.crypto.randomUUID(),
        })
      } else {
        setServerError(
          'There was an unknown error processing your request. Please refresh and try again.'
        )
      }
    } catch (error) {
      let errorMessage = error?.response
        ? error.response.data?.message || error.response.data
        : 'An error occurred. Please try again later.'

      if (typeof errorMessage === 'object' && errorMessage !== null) {
        errorMessage = 'An error occurred. Please try again later.'
      }

      if (error?.response?.status !== 409) {
        Sentry.captureMessage('handleSignup error', {
          extra: {
            data: error?.response?.data,
            status: error?.response?.status,
            error,
            errorMessage,
          },
        })
      }

      setServerError(errorMessage)
      // A genuine failure shouldn't permanently dead-end a retry path, so
      // release the guard here. The success path deliberately leaves it set.
      signupSubmittedRef.current = false
    }
    setSubmitting(false)
  }

  const handleModalUnmount = useCallback(() => {
    reset()
  }, [reset])

  const handleLeagueSelected = league => {
    setValue('team', null)
    if (league?.sport === 'other') {
      setValue('team', 'other')
      handleNextStep()
    }
  }

  const handleTeamSelected = team => {
    handleNextStep()
  }

  // Whether the HubSpot side-channel specifically can fire. Two ways it can't:
  // the visitor declined statistics cookies (the tracking hook is a no-op), or
  // HubSpot's script never loaded — react-hubspot-tracking-code-hook reads
  // `window._hsq` and silently falls back to a throwaway local array when it's
  // absent, which is exactly what an ad blocker stopping GTM produces. This no
  // longer gates whether Sales gets notified at all — see
  // handleInstantConnectInteraction — only whether HubSpot's
  // `lysted_instant_connect_*` properties also get updated.
  const canFulfillInstantConnect = () =>
    isStatisticsAllowed && typeof window !== 'undefined' && !!window._hsq

  // Sales notification for Instant Connect goes through our own backend
  // (`POST {REACT_APP_SIGNUP_URI}/signup/instant-connect`), independent of cookie
  // consent or whether GTM/HubSpot's script loaded — that's the whole point:
  // the old design relied solely on a HubSpot workflow watching a
  // client-side-only property change, which went silently missing for anyone
  // who declined statistics cookies. See INSTANT_CONNECT_SLACK_RELAY_PLAN.md
  // for the full contract this endpoint is expected to implement.
  // These options now live on the terminal step, so the contact has already
  // been identified by the signup-completion call before any of this runs.
  // The return value gates the confirmation UI: `true` only once the backend
  // call has actually succeeded, so InstantConnect never promises a callback
  // on a request that didn't go through.
  const handleInstantConnectInteraction = async (action, { phone } = {}) => {
    const email = getValues('email')
    if (!email) {
      return false
    }

    const data = getValues()
    let delivered = false
    try {
      await axios.post(
        `${process.env.REACT_APP_SIGNUP_URI}/signup/instant-connect`,
        {
          action,
          email,
          firstName: data.first,
          lastName: data.last,
          // Signup contact phone — always present, this form requires it.
          phone: formatPhoneNumberIntl(data.phone) || data.phone,
          // Only present for request_callback, and may differ from the
          // signup phone above — this is the number Sales should dial.
          ...(phone && {
            callbackPhone: formatPhoneNumberIntl(phone) || phone,
          }),
          ...(data.company && { company: data.company }),
          // Instant Connect only renders for large brokers, so this is
          // currently always the same value, but send it explicitly
          // rather than have the backend assume it.
          useCase: HUBSPOT_USE_CASE[data.seller_type],
          ...(data.sales_volume && { salesVolume: data.sales_volume }),
          // Informational only — backend's own receipt time is authoritative.
          clientSentAt: new Date().toISOString(),
        }
      )
      delivered = true
    } catch (e) {
      Sentry.captureMessage('INSTANT_CONNECT_BACKEND_FAILURE', {
        extra: {
          action,
          error: e?.toString(),
        },
      })
    }

    // Best-effort HubSpot side-channel, unaffected by the backend call's
    // outcome above — still gated on consent since it only works when the
    // tracking script actually loaded.
    if (canFulfillInstantConnect()) {
      try {
        setIdentity(email, {
          lysted_instant_connect_action: action,
          lysted_instant_connect_last_action_at: Date.now(),
          ...(phone && {
            lysted_instant_connect_callback_phone:
              formatPhoneNumberIntl(phone) || phone,
          }),
        })
        setTrackEvent({
          eventId: `${process.env.NODE_ENV !== 'production' ? 'TEST_' : ''}LYSTED_INSTANT_CONNECT`,
          value: action,
        })
        // Flush immediately (see the identical note on the signup-completion
        // identify call above) so HubSpot reflects this in real time rather
        // than whenever the visitor happens to trigger the next pageview.
        setPathPageView('/signup/instant-connect')
      } catch (e) {
        Sentry.captureMessage('INSTANT_CONNECT_TRACKING_FAILURE', {
          extra: {
            action,
            error: e?.toString(),
          },
        })
      }
    }

    return delivered
  }

  const handleNextStep = () => {
    const nextStep = currentStep + 1
    updateSteps()
    if (nextStep === VERIFICATION_STEP) {
      handleSignup()
    }
    document?.documentElement.scrollTo(0, 0)
  }

  // Step 3 (volume for Large, sport/team for Season) is the last step that
  // collects input for every seller type — the next step is terminal.
  const isFinalStep = step => step === 3

  // The sales options are a Large-broker-only upsell; every other seller type
  // sees the email-verification instructions on their own.
  const showInstantConnect = sellerTypeNextStep === 'large'

  const getHeaderText = () => {
    if (!!serverError) {
      return 'Whoops! There was an error'
    }
    if (currentStep === VERIFICATION_STEP && !submitting) {
      return `🎉 Great! We've received your request! 🎉`
    }

    return 'Sign up to get Lysted'
  }

  useEffect(() => {
    // Reset form on unmount
    return () => {
      handleModalUnmount()
    }
  }, [handleModalUnmount])

  return (
    <>
      <Helmet>
        <title>Lysted | Sign up to get Lysted</title>
        <link rel="canonical" href="https://lysted.com/signup" />
      </Helmet>
      <div
        className={cx('modal-content', {
          mobile: isMobile,
        })}
      >
        <div
          className={cx('modal-body', {
            mobile: isMobile,
          })}
        >
          <h1>{getHeaderText()}</h1>
          <div
            className={cx('step-container', {
              mobile: isMobile,
            })}
          >
            <StepNavigation
              steps={steps}
              currentStep={currentStep}
              onClose={() => navigate('/')}
              isMobile={isMobile}
            />
            <form
              onSubmit={handleSubmit(handleContinue)}
              className={cx({
                mobile: isMobile,
              })}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              />
              <div
                className={cx('form-container', 'signup-container', {
                  'mobile': isMobile,
                  'complete-merged':
                    currentStep === VERIFICATION_STEP &&
                    !submitting &&
                    !serverError &&
                    showInstantConnect,
                })}
              >
                {currentStep === 1 && (
                  <>
                    <FormSectionHeader
                      className="section-divider-custom"
                      title="Contact information"
                      isMobile={isMobile}
                    />
                    <div className="form-field">
                      <input
                        id="company"
                        name="company"
                        placeholder="Company name (optional)"
                        {...register('company', { required: false })}
                      />
                      <label htmlFor="company">
                        {'Company name (optional)'}
                      </label>
                    </div>
                    <div className="form-group">
                      <div
                        className={cx('form-field', {
                          error: errors.first,
                        })}
                      >
                        <input
                          id="first"
                          name="first"
                          placeholder="First name"
                          {...register('first', { required: true })}
                        />
                        <label htmlFor="first">First name</label>
                        {errors.first && (
                          <span className="error">Required</span>
                        )}
                      </div>
                      <div
                        className={cx('form-field', {
                          error: errors.last,
                        })}
                      >
                        <input
                          id="last"
                          name="last"
                          placeholder="Last name"
                          {...register('last', { required: true })}
                        />
                        <label htmlFor="last">Last name</label>
                        {errors.last && <span className="error">Required</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <div
                        className={cx('form-field', {
                          error: errors.email,
                        })}
                      >
                        <input
                          id="email"
                          type="email"
                          name="email"
                          placeholder="Email"
                          {...register('email', {
                            required: 'Required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                            maxLength: {
                              value: 96,
                              message: 'Email cannot exceed 96 characters',
                            },
                          })}
                        />
                        <label htmlFor="email">Email</label>
                        {errors.email && (
                          <span className="error">{errors.email.message}</span>
                        )}
                      </div>
                      <div
                        className={cx('form-field', {
                          error: errors.phone,
                        })}
                      >
                        <Controller
                          name="phone"
                          control={control}
                          rules={{
                            required: 'Required',
                            validate: value =>
                              isValidPhoneNumber(value) ||
                              'Invalid phone number',
                          }}
                          render={({ field: { onChange, value } }) => (
                            <PhoneInput
                              value={value}
                              onChange={v => {
                                onChange(v)
                                setValue('phone', v)
                                checkPhoneValue(v)
                                if (v) {
                                  clearErrors('phone')
                                }
                              }}
                              defaultCountry="US"
                              id="phone"
                              placeholder="Phone number"
                              name="phone"
                              className={cx({
                                'PhoneInput--reallyFocused': phoneIsFocused,
                              })}
                              onFocus={() => setPhoneIsFocused(true)}
                              onBlur={() => {
                                setPhoneIsFocused(false)
                                if (value && !isValidPhoneNumber(value)) {
                                  setError('phone', {
                                    type: 'validate',
                                    message: 'Invalid phone number',
                                  })
                                } else {
                                  clearErrors('phone')
                                }
                              }}
                            />
                          )}
                        />
                        <label
                          className={cx({
                            filled: phoneHasValue,
                            focused: phoneIsFocused && phoneHasValue,
                          })}
                          htmlFor="phone"
                        >
                          Phone number
                        </label>
                        {errors.phone && (
                          <span className="error">{errors.phone.message}</span>
                        )}
                      </div>
                    </div>
                    <FormSectionHeader
                      title="Mailing address"
                      rightNode={
                        <Tooltip
                          id="sign-up-form-address-explainer"
                          label={'Why do we collect this?'}
                          tooltipClassName="signup-tooltip"
                        >
                          <p>
                            {`We must collect your mailing address in order to process financial transactions such as ticket sales and payouts. Learn more by reading our `}
                            <Link
                              to={
                                'https://help.lysted.com/en/articles/8809352-terms-of-service'
                              }
                              target={'_blank'}
                              rel={'noopener noreferrer'}
                            >
                              terms of service
                            </Link>
                            {'.'}
                          </p>
                        </Tooltip>
                      }
                      isMobile={isMobile}
                    />
                    <div className="form-field">
                      <Controller
                        name="country"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <CountrySelector
                            value={field.value}
                            onChange={selectedOption => {
                              field.onChange(selectedOption)
                              setSelectedCountry(selectedOption)
                              clearErrors('phone')
                              setValue('state', null)
                              setValue('nationalId', null)
                              setSelectorStates({
                                country: {
                                  focused: selectorStates.country.focused,
                                  filled: !!selectedOption,
                                },
                                state: {
                                  focused: selectorStates.state.focused,
                                  filled: false,
                                },
                              })
                            }}
                            onFocus={() => {
                              setSelectorStates({
                                country: {
                                  focused: true,
                                  filled: selectorStates.country.filled,
                                },
                                state: {
                                  focused: false,
                                  filled: selectorStates.state.filled,
                                },
                              })
                            }}
                            onBlur={() => {
                              setSelectorStates({
                                country: {
                                  focused: false,
                                  filled: selectorStates.country.filled,
                                },
                                state: {
                                  focused: selectorStates.state.focused,
                                  filled: selectorStates.state.filled,
                                },
                              })
                            }}
                            className={cx('form-field', 'select', {
                              error: errors.country,
                            })}
                          />
                        )}
                      />
                      <label
                        className={cx({
                          filled: selectorStates.country.filled,
                          focused:
                            selectorStates.country.focused &&
                            selectorStates.country.filled,
                        })}
                        htmlFor="country"
                      >
                        Country
                      </label>
                      {errors.country && (
                        <span className="error">Please select a country</span>
                      )}
                    </div>
                    <div
                      className={cx('form-field', {
                        error: errors.address_1,
                      })}
                    >
                      <input
                        id="address_1"
                        name="address_1"
                        placeholder="Street and number, P.O. box, c/o."
                        {...register('address_1', { required: true })}
                      />
                      <label htmlFor="address_1">Street address</label>
                      {errors.address_1 && (
                        <span className="error">Required</span>
                      )}
                    </div>
                    <div className="form-field">
                      <input
                        id="address_2"
                        name="address_2"
                        placeholder="Apartment, suite, unit, building, floor, etc."
                        {...register('address_2', { required: false })}
                      />
                    </div>
                    <div className="form-group">
                      <div
                        className={cx('form-field', {
                          error: errors.city,
                        })}
                      >
                        <input
                          id="city"
                          name="city"
                          placeholder="City"
                          {...register('city', { required: true })}
                        />
                        <label htmlFor="city">City</label>
                        {errors.city && <span className="error">Required</span>}
                      </div>
                      <div className="form-field">
                        <Controller
                          name="state"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <StateSelector
                              country={selectedCountry}
                              value={field.value}
                              onChange={selectedOption => {
                                field.onChange(selectedOption)
                                setSelectorStates({
                                  country: {
                                    focused: selectorStates.country.focused,
                                    filled: selectorStates.country.filled,
                                  },
                                  state: {
                                    focused: selectorStates.state.focused,
                                    filled: !!selectedOption,
                                  },
                                })
                              }}
                              onFocus={() => {
                                setSelectorStates({
                                  country: {
                                    focused: false,
                                    filled: selectorStates.country.filled,
                                  },
                                  state: {
                                    focused: true,
                                    filled: selectorStates.state.filled,
                                  },
                                })
                              }}
                              onBlur={() => {
                                setSelectorStates({
                                  country: {
                                    focused: selectorStates.country.focused,
                                    filled: selectorStates.country.filled,
                                  },
                                  state: {
                                    focused: false,
                                    filled: selectorStates.state.filled,
                                  },
                                })
                              }}
                              className={cx('form-field', 'select', 'mid', {
                                error: errors.state,
                              })}
                            />
                          )}
                        />
                        <label
                          htmlFor="state"
                          className={cx({
                            filled: selectorStates.state.filled,
                            focused:
                              selectorStates.state.focused &&
                              selectorStates.state.filled,
                          })}
                        >
                          {'State / Province / Region'}
                        </label>
                        {errors.state && (
                          <span className="error">Required</span>
                        )}
                      </div>
                      <div
                        className={cx('form-field', {
                          error: errors.postal_code,
                        })}
                      >
                        <input
                          id="postal_code"
                          name="postal_code"
                          placeholder="Zip / Postal Code"
                          {...register('postal_code', {
                            validate: value =>
                              validatePostalCode(value, getValues),
                          })}
                        />
                        <label htmlFor="postal_code">Zip / Postal Code</label>
                        {errors.postal_code && (
                          <span className="error">
                            {errors.postal_code.message}
                          </span>
                        )}
                      </div>
                    </div>

                    <DOBSelector
                      isMobile={isMobile}
                      control={control}
                      setValue={setValue}
                      getValues={getValues}
                      errors={errors}
                    />

                    <NationalIdInput
                      control={control}
                      countryCode={selectedCountry.value}
                      isMobile={isMobile}
                      errors={errors}
                    />

                    <FormSectionHeader title="Terms" isMobile={isMobile} />
                    <div className="form-group">
                      <div
                        className={cx('form-field', {
                          error: errors.agreement,
                        })}
                      >
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            className="checkbox"
                            {...register('agreement', { required: true })}
                          />
                          <span className="checkbox-custom">
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              className="checked"
                            />
                            <FontAwesomeIcon
                              icon={faSquare}
                              className="unchecked"
                            />
                          </span>
                          I agree to Lysted's{' '}
                          <Link
                            to="https://help.lysted.com/en/articles/8809352-terms-of-service"
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            Terms of Service
                          </Link>
                        </label>
                        {errors.agreement && (
                          <span className="error">
                            You must agree to the terms of service
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="form-field">
                        <label className="checkbox-label mt-8">
                          <input
                            type="checkbox"
                            className="checkbox"
                            {...register('consent_informational')}
                          />
                          <span className="checkbox-custom">
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              className="checked"
                            />
                            <FontAwesomeIcon
                              icon={faSquare}
                              className="unchecked"
                            />
                          </span>
                          <span>
                            I agree to receive <strong>informational</strong>{' '}
                            communications from Lysted about its products and
                            services via phone calls, SMS/text messages, and
                            other channels using the contact details I provided.
                            This consent is not required to use the services.
                            See our{' '}
                            <Link
                              to="https://help.lysted.com/en/articles/8809352-terms-of-service"
                              target={'_blank'}
                              rel={'noopener noreferrer'}
                            >
                              Terms of Use
                            </Link>{' '}
                            and{' '}
                            <Link
                              to="https://help.lysted.com/en/articles/16763214-privacy-policy"
                              target={'_blank'}
                              rel={'noopener noreferrer'}
                            >
                              Privacy Policy
                            </Link>{' '}
                            for details.{' '}
                            <em>
                              Message frequency varies. Msg &amp; data rates may
                              apply. Reply HELP for help and STOP to cancel.
                            </em>
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="form-field">
                        <label className="checkbox-label mt-8">
                          <input
                            type="checkbox"
                            className="checkbox"
                            {...register('consent_comms')}
                          />
                          <span className="checkbox-custom">
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              className="checked"
                            />
                            <FontAwesomeIcon
                              icon={faSquare}
                              className="unchecked"
                            />
                          </span>
                          <span>
                            I agree to receive marketing communications from
                            Lysted about its products and services via email,
                            phone calls, SMS/text messages, push notifications,
                            and other channels using the contact details I
                            provided. This consent is not required to use the
                            services. See our{' '}
                            <Link
                              to="https://help.lysted.com/en/articles/8809352-terms-of-service"
                              target={'_blank'}
                              rel={'noopener noreferrer'}
                            >
                              Terms of Use
                            </Link>{' '}
                            and{' '}
                            <Link
                              to="https://help.lysted.com/en/articles/16763214-privacy-policy"
                              target={'_blank'}
                              rel={'noopener noreferrer'}
                            >
                              Privacy Policy
                            </Link>{' '}
                            for details.{' '}
                            <em>
                              Message frequency varies. Msg &amp; data rates may
                              apply. Reply HELP for help and STOP to cancel.
                            </em>
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="form-group">
                      <div className={cx('form-field')}>
                        <div className={'recaptcha-privacy-terms'}>
                          This form is protected by reCAPTCHA.{' '}
                          <Link
                            to="https://policies.google.com/privacy"
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            Google's Privacy Policy
                          </Link>{' '}
                          and{' '}
                          <Link
                            to="https://policies.google.com/terms"
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            Google's Terms of Service
                          </Link>{' '}
                          apply.
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {currentStep === 2 && (
                  <>
                    <FormSectionHeader
                      className="section-divider-custom"
                      title={
                        isMobile
                          ? 'Sellery category?'
                          : 'What is your ticket seller category?'
                      }
                      rightNode={
                        <Tooltip
                          id="sign-up-form-use-case-explainer"
                          label={'Why do we collect this?'}
                          place="bottom"
                          tooltipClassName="signup-tooltip"
                        >
                          <span>
                            {`By indicating your ticket seller category, we can provide you better training and support and enhance your user experience on Lysted.`}
                          </span>
                        </Tooltip>
                      }
                      isMobile={isMobile}
                    />
                    <BlockOptions
                      name="seller_type"
                      options={sellerOptions}
                      control={control}
                      errors={errors}
                      onOptionSelected={handleSellerTypeSelected}
                      isMobile={isMobile}
                    />
                  </>
                )}
                {currentStep === 3 && (
                  <>
                    {sellerTypeNextStep === 'large' ? (
                      <>
                        <FormSectionHeader
                          className="section-divider-custom"
                          title={
                            isMobile
                              ? 'Annual sales volume?'
                              : 'What is your approximate annual sales volume?'
                          }
                          rightNode={
                            <Tooltip
                              id="sign-up-form-use-case-explainer"
                              label={'Why do we collect this?'}
                              place="bottom"
                              tooltipClassName="signup-tooltip"
                            >
                              <span>
                                {`By indicating your approximate yearly sales volume, we can provide you better training and support and enhance your user experience on Lysted.`}
                              </span>
                            </Tooltip>
                          }
                          isMobile={isMobile}
                        />
                        <BlockOptions
                          name="sales_volume"
                          options={volumeOptions}
                          control={control}
                          errors={errors}
                          onOptionSelected={handleVolumeSelected}
                          isMobile={isMobile}
                        />
                      </>
                    ) : (
                      <SportSelector
                        control={control}
                        errors={errors}
                        onLeagueSelected={handleLeagueSelected}
                        onTeamSelected={handleTeamSelected}
                        isMobile={isMobile}
                      />
                    )}
                  </>
                )}
                {currentStep === VERIFICATION_STEP && (
                  <>
                    {submitting ? (
                      <LoadingSpinner />
                    ) : !serverError ? (
                      <>
                        <Confetti />
                        {showInstantConnect ? (
                          /* Large brokers get the verification instructions and the
                                                       sales options as one numbered checklist: the account is
                                                       already created by the time they land here, so talking to
                                                       Sales is purely additive and can't strand the signup. */
                          <>
                            <p className="complete-lead">
                              Your account request is in. Just two quick things
                              to finish getting set up:
                            </p>
                            <ol className="complete-steps">
                              <li className="complete-step step-confirm">
                                <div className="step-badge">1</div>
                                <div className="step-content">
                                  <div className="step-heading">
                                    <h2>Check your email</h2>
                                  </div>
                                  <div className="step-card confirm-card">
                                    <p className="confirmation-copy">
                                      {`We've sent a verification link to `}
                                      <strong>{getValues('email')}</strong>
                                      {`. Click it to confirm your email and create your password.`}
                                    </p>
                                    <p className="confirmation-spam">
                                      {`Didn't receive it? Check your Spam folder, or contact support.`}
                                    </p>
                                  </div>
                                </div>
                              </li>
                              <li className="complete-step step-connect">
                                <div className="step-badge">2</div>
                                <div className="step-content">
                                  <div className="step-heading">
                                    <h2>Talk to our team</h2>
                                    <span className="step-pill">Optional</span>
                                  </div>
                                  <InstantConnect
                                    isMobile={isMobile}
                                    phone={getValues('phone')}
                                    firstName={getValues('first')}
                                    lastName={getValues('last')}
                                    email={getValues('email')}
                                    onInteraction={
                                      handleInstantConnectInteraction
                                    }
                                  />
                                </div>
                              </li>
                            </ol>
                            <div
                              onClick={() => navigate('/')}
                              className="btn sign-up-complete"
                            >
                              Return to Lysted
                            </div>
                          </>
                        ) : (
                          <div className="form-field completed">
                            <h2>Please check your email</h2>
                            <span className="next-steps">
                              In a few moments an email will be sent to{' '}
                              <strong>{getValues('email')}</strong> with a link
                              to verify your email and create a password.
                            </span>
                            <div
                              onClick={() => navigate('/')}
                              className="btn sign-up-complete"
                            >
                              Return to Lysted
                            </div>
                            <span className="next-steps-spam">
                              {`Didn't receive the email? Please check your Spam folder. If you don't find it there, contact support.`}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="form-field completed">
                        <h2>Unable to complete Sign up</h2>
                        <span className="next-steps error">{serverError}</span>
                        <div
                          onClick={() => navigate('/')}
                          className="btn sign-up-complete"
                        >
                          Return to Lysted
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              {currentStep < VERIFICATION_STEP && (
                <div
                  className={cx('button-group', {
                    mobile: isMobile,
                  })}
                >
                  <button
                    type="button"
                    className="back-button"
                    onClick={handleBack}
                  >
                    {currentStep === 1 ? (
                      'Cancel'
                    ) : (
                      <>
                        <FontAwesomeIcon
                          size="lg"
                          icon={faChevronLeft}
                          className="chevron-icon-left"
                        />
                        {'Back'}
                      </>
                    )}
                  </button>
                  <button type="submit">
                    {isFinalStep(currentStep) ? 'Complete' : 'Continue'}
                    <FontAwesomeIcon
                      size="lg"
                      icon={faChevronRight}
                      className="chevron-icon"
                    />
                  </button>
                </div>
              )}
            </form>
          </div>
          <div className={'existing-account'}>
            Already have an account?{' '}
            <LogIn className={'link'} location={location} />
          </div>
        </div>
      </div>
    </>
  )
}

export default Signup
